import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import {
  AssistantPanelMode,
  AssistantPanelVariant,
  AssistantPanelFloatingPosition,
  AssistantPanelFloatingSize,
} from "./types";
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  FLOATING_DEFAULT_WIDTH,
  FLOATING_DEFAULT_HEIGHT,
  FLOATING_MIN_WIDTH,
  FLOATING_MAX_WIDTH,
  FLOATING_MIN_HEIGHT,
  FLOATING_MAX_HEIGHT,
} from "./constants";

const getDefaultFloatingPosition = (): AssistantPanelFloatingPosition => ({
  x: Math.max(0, (window.innerWidth - FLOATING_DEFAULT_WIDTH) / 2),
  y: Math.max(0, (window.innerHeight - FLOATING_DEFAULT_HEIGHT) / 2),
});

const getDefaultFloatingSize = (): AssistantPanelFloatingSize => ({
  width: FLOATING_DEFAULT_WIDTH,
  height: FLOATING_DEFAULT_HEIGHT,
});

type AssistantPanelStoreState = {
  isOpen: boolean;
  preferredVariant: AssistantPanelVariant | undefined;
  variant: AssistantPanelVariant;
  width: number;
  floatingPosition: AssistantPanelFloatingPosition;
  floatingSize: AssistantPanelFloatingSize;
  isSidebarResizing: boolean;
  mode: AssistantPanelMode;
};

type AssistantPanelStoreActions = {
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  setFloatingPosition: (position: AssistantPanelFloatingPosition) => void;
  setFloatingSize: (size: AssistantPanelFloatingSize) => void;
  setIsSidebarResizing: (isSidebarResizing: boolean) => void;
  pin: () => void;
  unpin: () => void;
  resetFloatingLayout: () => void;
  switchMode: (mode: AssistantPanelMode) => void;
  initialize: (isSmallScreen: boolean) => void;
  onScreenBreakpointChange: (isSmallScreen: boolean) => void;
};

type AssistantPanelStore = AssistantPanelStoreState & AssistantPanelStoreActions;

const useAssistantPanelStore = create<AssistantPanelStore>()(
  persist(
    immer((set, get) => ({
      isOpen: false,
      preferredVariant: undefined,
      variant: "side",
      width: SIDEBAR_DEFAULT_WIDTH,
      floatingPosition: getDefaultFloatingPosition(),
      floatingSize: getDefaultFloatingSize(),
      isSidebarResizing: false,
      mode: AssistantPanelMode.HOME,

      setIsOpen: (open) => {
        set((state) => {
          state.isOpen = open;
        });
      },

      toggle: () => {
        set((state) => {
          state.isOpen = !state.isOpen;
        });
      },

      setWidth: (width) => {
        set((state) => {
          state.width = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, width));
        });
      },

      setFloatingPosition: (position) => {
        set((state) => {
          state.floatingPosition = {
            x: Math.max(0, Math.min(window.innerWidth - state.floatingSize.width, position.x)),
            y: Math.max(0, Math.min(window.innerHeight - state.floatingSize.height, position.y)),
          };
        });
      },

      setFloatingSize: (size) => {
        set((state) => {
          state.floatingSize = {
            width: Math.max(FLOATING_MIN_WIDTH, Math.min(FLOATING_MAX_WIDTH, size.width)),
            height: Math.max(FLOATING_MIN_HEIGHT, Math.min(FLOATING_MAX_HEIGHT, size.height)),
          };
        });
      },

      setIsSidebarResizing: (isResizing) => {
        set((state) => {
          state.isSidebarResizing = isResizing;
        });
      },

      pin: () => {
        set((state) => {
          state.preferredVariant = "side";
          state.variant = state.preferredVariant;
        });
      },

      unpin: () => {
        set((state) => {
          state.preferredVariant = "floating";
          state.variant = state.preferredVariant;
        });
        get().resetFloatingLayout();
      },

      resetFloatingLayout: () => {
        set((state) => {
          state.floatingPosition = getDefaultFloatingPosition();
          state.floatingSize = getDefaultFloatingSize();
        });
      },

      switchMode: (mode) => {
        set((state) => {
          state.mode = mode;
        });
      },

      initialize: (isSmallScreen: boolean) => {
        set((state) => {
          if (!state.preferredVariant) {
            state.variant = isSmallScreen ? "floating" : "side";
          }
        });
      },

      onScreenBreakpointChange: (isSmallScreen: boolean) => {
        const wasInSideMode = get().variant === "side";
        set((state) => {
          if (isSmallScreen) {
            state.variant = "floating";
          } else {
            state.variant = state.preferredVariant ?? "side";
          }
        });

        if (isSmallScreen && wasInSideMode) {
          get().resetFloatingLayout();
        }
      },
    })),
    {
      name: "assistant-panel-layout-store",
      version: 2,
      partialize: (state) => ({
        isOpen: state.isOpen,
        variant: state.variant,
        preferredVariant: state.preferredVariant,
        width: state.width,
        floatingPosition: state.floatingPosition,
        floatingSize: state.floatingSize,
        mode: state.mode,
      }),
    }
  )
);

export default useAssistantPanelStore;
